import {
  DropDownDto,
  OrganizationResponseDto,
} from "../components/common/ObjectTypes";
import {
  OrganizationSearchQueryType,
  OrgUserSearchQueryType,
} from "../components/common/QueryTypes";
import axios from "./axios";

export async function getOrganization(
  orgId: number,
): Promise<OrganizationResponseDto | undefined> {
  try {
    const resp = await axios.get(`/organizations/${orgId}`);
    return resp.data;
  } catch (err) {
    console.error(err);
  }
}

/**
 * Read one organization by its guid.
 *
 * Rethrows the interceptor's rejection rather than swallowing it (AKL-939): the API answers
 * 403 for a user without VIEW_ORG_DETAILS, and the details page tells that apart from "not
 * found" to show the no-access state. Every caller sits in a try/catch that already treated
 * `undefined` as a failure, so nothing else changes for them.
 * @param orgGuid the organization's guid
 * @returns the organization; `undefined` only if the API answered 2xx with no body
 */
export async function getOrganizationByGuid(
  orgGuid: string,
): Promise<OrganizationResponseDto | undefined> {
  try {
    const resp = await axios.get(`/organizations/guid/${orgGuid}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getOrganizations(
  query: OrganizationSearchQueryType,
): Promise<{ organizations: OrganizationResponseDto[]; count: number }> {
  try {
    const resp = await axios.get("/organizations", {
      params: query,
    });
    return resp.data;
  } catch (error) {
    console.error("Error fetching organizations:", error);
    throw error; // Rethrow the error to be handled by the caller
  }
}

export async function getParentOrganizationOptions(
  child_org_id: number,
): Promise<OrganizationResponseDto[]> {
  try {
    const resp = await axios.get(
      `/organizations/${child_org_id}/parentoptions`,
    );
    return resp.data;
  } catch (error) {
    console.error("Error fetching organizations:", error);
    throw error; // Rethrow the error to be handled by the caller
  }
}

export async function getProjectOrganizationOptions(): Promise<DropDownDto[]> {
  try {
    const resp = await axios.get(`/organizations/projectoptions`);
    return resp.data;
  } catch (error) {
    console.error("Error fetching organizations:", error);
    throw error; // Rethrow the error to be handled by the caller
  }
}

export async function createOrganization(
  data: any,
): Promise<OrganizationResponseDto> {
  try {
    const resp = await axios.post("/organizations", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err; // Rethrow the error to handle it in the calling code if needed
  }
}

export async function updateOrganization(
  orgId: number,
  data: any,
): Promise<OrganizationResponseDto> {
  try {
    const resp = await axios.put(`/organizations/${orgId}`, data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getOrganizationUsers(
  query: OrgUserSearchQueryType,
): Promise<{ users: any[]; count: number }> {
  try {
    const resp = await axios.get(`/organizations/${query.org_id}/users`, {
      params: query,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function createOrganizationUser(org_id: number, data: any) {
  try {
    const resp = await axios.post(
      `/organizations/${org_id}/users/invite`,
      data,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function reinviteOrganizationUser(
  org_id: number,
  user_id: string,
  data: any,
) {
  try {
    const resp = await axios.post(
      `/organizations/${org_id}/users/${user_id}/reinvite`,
      data,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getUserRoles(org_id: number) {
  try {
    const resp = await axios.get(`/organizations/${org_id}/roles`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getOrganizationPermissions(orgId: number) {
  try {
    const resp = await axios.get(`/organizations/${orgId}/permissions`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getCountries(language: string) {
  try {
    const resp = await axios.get(`/organizations/countries/${language}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function updateOrgUserRole(
  org_id: number,
  user_id: string,
  data: any,
) {
  try {
    const resp = await axios.put(
      `/organizations/${org_id}/users/${user_id}/roles`,
      data,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function removeUserFromOrganization(
  org_id: number,
  user_id: string,
) {
  try {
    const resp = await axios.delete(
      `/organizations/${org_id}/users/${user_id}`,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getParentOrganizationName(
  orgId: number,
): Promise<string> {
  try {
    const resp = await axios.get(`/organizations/${orgId}/parent_name`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}
